import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  TableContainer,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  IconButton,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import api from 'services/api';
import { Client } from 'services/user';
import DataTable from 'components/Shared/Table';
import Loader from 'components/Shared/Loader';
import type { MUIDataTableState } from 'mui-datatables';
import { primary } from 'helpers/colors';

type Props = {
  accessGroupId: number;
  onRefresh: () => void;
};

interface AccessGroupProfile {
  id: number;
  name: string;
}

interface AccessGroupProfileWithClients extends AccessGroupProfile {
  clients?: Client[];
}

function ProfilesTab({ accessGroupId, onRefresh }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [profiles, setProfiles] = useState<AccessGroupProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AccessGroupProfile | null>(null);
  const [detailsSelectedClients, setDetailsSelectedClients] = useState<Client[]>([]);
  const [detailsClientOptions, setDetailsClientOptions] = useState<Client[]>([]);
  const [detailsCheckedClientIds, setDetailsCheckedClientIds] = useState<number[]>([]);
  const [detailsModalSearch, setDetailsModalSearch] = useState('');
  const [isFetchingProfileDetails, setIsFetchingProfileDetails] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);

  const [newProfileCheckedIds, setNewProfileCheckedIds] = useState<number[]>([]);
  const [newProfileSearch, setNewProfileSearch] = useState('');
  const [modalClients, setModalClients] = useState<Client[]>([]);
  const [isLoadingModalClients, setIsLoadingModalClients] = useState(false);

  const [profileToDelete, setProfileToDelete] = useState<AccessGroupProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const lastLoadedProfileIdRef = useRef<number | null>(null);

  const loadProfiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<any>(`/access_groups/${accessGroupId}/profiles`, {
        perPage: rowsPerPage,
        page: page,
        search: searchText,
        order: sortField,
        direction: sortDirection
      });

      if (response.data) {
        const profileList = response.data.data || response.data;
        const total = response.data.total || (Array.isArray(profileList) ? profileList.length : 0);
        setProfiles(profileList);
        setTotalCount(total);
      }
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar('Erro ao carregar perfis', { variant: 'error' });
      setIsLoading(false);
    }
  }, [accessGroupId, enqueueSnackbar, page, rowsPerPage, searchText, sortField, sortDirection]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const fetchProfileDetails = useCallback(
    async (profile: AccessGroupProfile) => {
      setIsFetchingProfileDetails(true);
      try {
        const [profileResponse, clientsResponse] = await Promise.all([
          api.get<{ data: AccessGroupProfileWithClients }>(`/access_groups/profiles/${profile.id}`),
          api.get<{ data: { clients: Client[]; total: number } }>(`/access_groups/${accessGroupId}/clients`, {
            perPage: 9999,
            page: 1
          })
        ]);

        const profileClients = profileResponse.data?.clients || [];
        const allClients = clientsResponse.data?.clients || clientsResponse.data || [];
        const allClientsArr: Client[] = Array.isArray(allClients) ? allClients : [];
        const selectedIds = new Set(profileClients.map((c: Client) => c.id));
        const ordered = [
          ...allClientsArr.filter(c => selectedIds.has(c.id)),
          ...allClientsArr.filter(c => !selectedIds.has(c.id))
        ];
        setDetailsSelectedClients(profileClients);
        setDetailsClientOptions(ordered);
        if (lastLoadedProfileIdRef.current !== profile.id) {
          setDetailsCheckedClientIds(profileClients.map((c: Client) => c.id));
        }
        lastLoadedProfileIdRef.current = profile.id;
      } catch (error) {
        enqueueSnackbar('Erro ao carregar detalhes do perfil', { variant: 'error' });
        setDetailsSelectedClients([]);
        setDetailsClientOptions([]);
      } finally {
        setIsFetchingProfileDetails(false);
      }
    },
    [accessGroupId, enqueueSnackbar]
  );

  const handleOpenDetails = useCallback(
    (profile: AccessGroupProfile) => {
      setSelectedProfile(profile);
      setDetailsSelectedClients([]);
      setDetailsModalOpen(true);
      fetchProfileDetails(profile);
    },
    [fetchProfileDetails]
  );

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setSelectedProfile(null);
    setDetailsSelectedClients([]);
    setDetailsClientOptions([]);
    setDetailsCheckedClientIds([]);
    setDetailsModalSearch('');
    lastLoadedProfileIdRef.current = null;
  };

  const saveClients = useCallback(
    async (clients: Client[]) => {
      if (!selectedProfile) return;
      setIsAddingClient(true);
      try {
        await api.patch(`/access_groups/profiles/${selectedProfile.id}`, {
          name: selectedProfile.name,
          clients: clients.map(c => c.id)
        });
        setDetailsSelectedClients(clients);
        enqueueSnackbar('CNPJs do perfil atualizados com sucesso', { variant: 'success' });
        loadProfiles();
        onRefresh();
      } catch (error: any) {
        const apiMessage = error?.response?.data?.message;
        enqueueSnackbar(apiMessage || 'Erro ao atualizar CNPJs do perfil', { variant: 'error' });
      } finally {
        setIsAddingClient(false);
      }
    },
    [selectedProfile, enqueueSnackbar, loadProfiles, onRefresh]
  );

  const handleConfirmAddClients = async () => {
    const newClients = detailsClientOptions.filter(o => detailsCheckedClientIds.includes(o.id));
    await saveClients(newClients);
  };

  const formatCNPJ = (cnpj: string) => cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');

  const handleAddClick = async () => {
    setAddModalOpen(true);
    setIsLoadingModalClients(true);
    try {
      const response = await api.get<{ data: { clients: Client[]; total: number } }>(
        `/access_groups/${accessGroupId}/clients`,
        { perPage: 9999, page: 1 }
      );
      setModalClients(response.data?.clients || []);
    } catch {
      enqueueSnackbar('Erro ao carregar CNPJs', { variant: 'error' });
      setModalClients([]);
    } finally {
      setIsLoadingModalClients(false);
    }
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewProfileName('');
    setNewProfileCheckedIds([]);
    setNewProfileSearch('');
    setModalClients([]);
  };

  const handleCreateProfile = async () => {
    if (!newProfileName.trim()) {
      enqueueSnackbar('Digite um nome para o perfil', { variant: 'warning' });
      return;
    }
    if (newProfileCheckedIds.length === 0) {
      enqueueSnackbar('Selecione pelo menos um CNPJ', { variant: 'warning' });
      return;
    }
    setIsCreating(true);
    try {
      await api.post(`/access_groups/${accessGroupId}/profiles`, {
        name: newProfileName,
        clients: newProfileCheckedIds
      });
      enqueueSnackbar('Perfil criado com sucesso', { variant: 'success' });
      handleCloseAddModal();
      loadProfiles();
      onRefresh();
    } catch (error) {
      enqueueSnackbar('Erro ao criar perfil', { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const confirmDeleteProfile = useCallback(async () => {
    if (!profileToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/access_groups/profiles/${profileToDelete.id}`);
      enqueueSnackbar('Perfil removido com sucesso', { variant: 'success' });
      setProfileToDelete(null);
      if (selectedProfile?.id === profileToDelete.id) {
        setDetailsModalOpen(false);
        setSelectedProfile(null);
        setDetailsSelectedClients([]);
      }
      loadProfiles();
      onRefresh();
    } catch (error) {
      enqueueSnackbar('Erro ao remover perfil', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [profileToDelete, selectedProfile, enqueueSnackbar, loadProfiles, onRefresh]);

  const formattedData = useMemo(() => [...profiles], [profiles]);

  const renderActions = useCallback(
    (_value: number, tableMeta: any) => {
      const profile = profiles[tableMeta.rowIndex];
      if (!profile) return null;
      return (
        <Box display='flex' justifyContent='flex-start' gap={0.5}>
          <Tooltip title='Editar CNPJs'>
            <IconButton
              size='small'
              color='primary'
              onClick={e => {
                e.stopPropagation();
                handleOpenDetails(profile);
              }}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Remover'>
            <IconButton
              size='small'
              color='error'
              onClick={e => {
                e.stopPropagation();
                setProfileToDelete(profile);
              }}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    [profiles, handleOpenDetails]
  );

  const columns = useMemo(
    () => [
      {
        name: 'name',
        label: 'Nome',
        options: {
          sort: true,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'id',
        label: 'Ações',
        options: {
          sort: false,
          customBodyRender: renderActions,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      }
    ],
    [renderActions]
  );

  const handleTableChange = useCallback((action: string, tableState: MUIDataTableState) => {
    switch (action) {
      case 'changePage':
        setPage(tableState.page + 1);
        break;
      case 'changeRowsPerPage':
        setRowsPerPage(tableState.rowsPerPage);
        setPage(1);
        break;
      case 'search':
        setSearchText(tableState.searchText || '');
        setPage(1);
        break;
      case 'sort':
        if (tableState.sortOrder?.name && tableState.sortOrder?.direction) {
          setSortField(tableState.sortOrder.name);
          setSortDirection(tableState.sortOrder.direction);
          setPage(1);
        }
        break;
      default:
        console.info(`action (${action}) not handled.`);
    }
  }, []);

  const optionsObj = useMemo(
    () => ({
      page: page - 1,
      count: totalCount,
      rowsPerPage,
      jumpToPage: true,
      onTableChange: handleTableChange,
      onRowClick: (rowData: any, rowMeta: any) => {
        const idx = rowMeta?.dataIndex;
        if (typeof idx === 'number' && profiles[idx]) {
          handleOpenDetails(profiles[idx]);
        }
      },
      setRowProps: () => ({ style: { cursor: 'pointer' } }),
      serverSide: true,
      search: false,
      searchText,
      selectableRows: 'none',
      sortOrder: { name: sortField, direction: sortDirection }
    }),
    [
      page,
      totalCount,
      rowsPerPage,
      handleTableChange,
      searchText,
      sortField,
      sortDirection,
      profiles,
      handleOpenDetails
    ]
  );

  const hasDetailsChanges = useMemo(() => {
    const savedIds = detailsSelectedClients.map(c => c.id);
    if (savedIds.length !== detailsCheckedClientIds.length) return true;
    const checkedSet = new Set(detailsCheckedClientIds);
    return savedIds.some(id => !checkedSet.has(id));
  }, [detailsSelectedClients, detailsCheckedClientIds]);

  return (
    <Box>
      <Box mb={0} display='flex' mx={2} gap={2} alignItems='center'>
        <TextField
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          placeholder='Pesquisar por Nome do Perfil'
          variant='outlined'
          size='small'
          sx={{ input: { fontSize: '0.9rem' }, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
        />
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disableElevation
          sx={{ borderRadius: 10, whiteSpace: 'nowrap', py: 0.7, '&:hover': { backgroundColor: primary.hover } }}
        >
          Criar Perfil
        </Button>
      </Box>

      <Card elevation={0}>
        <CardContent>
          <TableContainer elevation={0} component={Paper}>
            {isLoading ? <Loader /> : <DataTable columns={columns} data={formattedData} optionsObj={optionsObj} />}
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={addModalOpen} onClose={!isCreating ? handleCloseAddModal : undefined} maxWidth='sm' fullWidth>
        <DialogTitle>Criar Novo Perfil</DialogTitle>
        <DialogContent dividers>
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            <TextField
              label='Nome do Perfil *'
              value={newProfileName}
              onChange={e => setNewProfileName(e.target.value)}
              fullWidth
              autoFocus
              size='small'
              disabled={isCreating}
              sx={{ input: { fontSize: '0.9rem' }, '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
            />
            <Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <TextField
                  fullWidth
                  size='small'
                  placeholder='Buscar por CNPJ ou Razão Social'
                  value={newProfileSearch}
                  onChange={e => setNewProfileSearch(e.target.value)}
                  disabled={isCreating || isLoadingModalClients}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' color='action' />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    input: { fontSize: '0.9rem' },
                    flex: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: 10 },
                    px: 1.5,
                    pt: 1.5,
                    pb: 0.5
                  }}
                />

                {(() => {
                  if (isLoadingModalClients) {
                    return (
                      <Box display='flex' justifyContent='center' py={4}>
                        <CircularProgress size={24} />
                      </Box>
                    );
                  }
                  if (modalClients.length === 0) {
                    return (
                      <Box py={4} px={1.5} mx={1.5} textAlign='center'>
                        <Typography variant='body2' color='text.secondary'>
                          Nenhum CNPJ disponível
                        </Typography>
                      </Box>
                    );
                  }
                  const q = newProfileSearch.toLowerCase();
                  const filtered = modalClients.filter(
                    c =>
                      c.cnpj.includes(q) ||
                      c.razao_social.toLowerCase().includes(q) ||
                      (c.nickname || '').toLowerCase().includes(q)
                  );
                  if (filtered.length === 0) {
                    return (
                      <Box py={4} px={1.5} mx={1.5} textAlign='center'>
                        <Typography variant='body2' color='text.secondary'>
                          Nenhum CNPJ encontrado
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto', px: 1.5 }}>
                      {filtered.map(client => (
                        <ListItemButton
                          key={client.id}
                          onClick={() =>
                            setNewProfileCheckedIds(prev =>
                              prev.includes(client.id) ? prev.filter(id => id !== client.id) : [...prev, client.id]
                            )
                          }
                          disabled={isCreating}
                          sx={{ px: 1.5 }}
                        >
                          <Checkbox
                            edge='start'
                            checked={newProfileCheckedIds.includes(client.id)}
                            tabIndex={-1}
                            disableRipple
                            size='small'
                            sx={{ mr: 1 }}
                          />
                          <ListItemText
                            primary={client.nickname || client.razao_social}
                            secondary={`${formatCNPJ(client.cnpj)}${
                              client.nickname ? ` — ${client.razao_social}` : ''
                            }`}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  );
                })()}
              </Box>
              <Box display='flex' alignItems='center' justifyContent='space-between' mt={0.5}>
                <Typography
                  variant='caption'
                  sx={{ color: newProfileCheckedIds.length === 0 ? 'warning.main' : 'text.secondary' }}
                >
                  {newProfileCheckedIds.length === 0
                    ? 'Selecione pelo menos 1 CNPJ'
                    : `${newProfileCheckedIds.length} CNPJ${newProfileCheckedIds.length > 1 ? 's' : ''} selecionado${
                        newProfileCheckedIds.length > 1 ? 's' : ''
                      }`}
                </Typography>
                {!isLoadingModalClients &&
                  modalClients.length > 0 &&
                  (() => {
                    const q = newProfileSearch.toLowerCase();
                    const filtered = modalClients.filter(
                      c =>
                        c.cnpj.includes(q) ||
                        c.razao_social.toLowerCase().includes(q) ||
                        (c.nickname || '').toLowerCase().includes(q)
                    );
                    if (filtered.length === 0) return null;
                    const allSelected = filtered.every(c => newProfileCheckedIds.includes(c.id));
                    return (
                      <Button
                        size='small'
                        disabled={isCreating}
                        onClick={() => {
                          if (allSelected) {
                            setNewProfileCheckedIds(prev => prev.filter(id => !filtered.some(c => c.id === id)));
                          } else {
                            setNewProfileCheckedIds(prev => Array.from(new Set([...prev, ...filtered.map(c => c.id)])));
                          }
                        }}
                      >
                        {allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                      </Button>
                    );
                  })()}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '14px 20px' }}>
          <Button onClick={handleCloseAddModal} disabled={isCreating}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleCreateProfile}
            disabled={isCreating || !newProfileName.trim() || newProfileCheckedIds.length === 0}
            disableElevation
            startIcon={isCreating ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap', py: 0.7, '&:hover': { backgroundColor: primary.hover } }}
          >
            {isCreating ? 'Criando...' : 'Criar Perfil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsModalOpen}
        onClose={!isAddingClient ? handleCloseDetails : undefined}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>CNPJs do Perfil: {selectedProfile?.name}</DialogTitle>
        <DialogContent dividers>
          <Box>
            {isFetchingProfileDetails ? (
              <Box display='flex' justifyContent='center' py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  size='small'
                  placeholder='Buscar por CNPJ ou Razão Social'
                  value={detailsModalSearch}
                  onChange={e => setDetailsModalSearch(e.target.value)}
                  disabled={isAddingClient}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' color='action' />
                      </InputAdornment>
                    )
                  }}
                  sx={{ input: { fontSize: '0.9rem' }, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
                />
                <Box mt={1.5} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  {detailsClientOptions.length === 0 && (
                    <Box py={4} textAlign='center'>
                      <Typography variant='body2' color='text.secondary'>
                        Nenhum CNPJ disponível
                      </Typography>
                    </Box>
                  )}
                  {detailsClientOptions.length > 0 &&
                    (() => {
                      const q = detailsModalSearch.toLowerCase();
                      const filtered = detailsClientOptions.filter(
                        c =>
                          c.cnpj.includes(q) ||
                          c.razao_social.toLowerCase().includes(q) ||
                          (c.nickname || '').toLowerCase().includes(q)
                      );
                      return filtered.length === 0 ? (
                        <Box py={4} textAlign='center'>
                          <Typography variant='body2' color='text.secondary'>
                            Nenhum CNPJ encontrado
                          </Typography>
                        </Box>
                      ) : (
                        <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
                          {filtered.map(client => (
                            <ListItemButton
                              key={client.id}
                              onClick={() =>
                                setDetailsCheckedClientIds(prev =>
                                  prev.includes(client.id) ? prev.filter(id => id !== client.id) : [...prev, client.id]
                                )
                              }
                              disabled={isAddingClient}
                              sx={{ px: 1.5 }}
                            >
                              <Checkbox
                                edge='start'
                                checked={detailsCheckedClientIds.includes(client.id)}
                                tabIndex={-1}
                                disableRipple
                                size='small'
                                sx={{ mr: 1 }}
                              />
                              <ListItemText
                                primary={client.nickname || client.razao_social}
                                secondary={`${formatCNPJ(client.cnpj)}${
                                  client.nickname ? ` — ${client.razao_social}` : ''
                                }`}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      );
                    })()}
                </Box>
                <Box display='flex' alignItems='center' justifyContent='space-between' mt={0.5}>
                  <Typography variant='caption' color='text.secondary'>
                    {detailsCheckedClientIds.length > 0
                      ? `${detailsCheckedClientIds.length} CNPJ${
                          detailsCheckedClientIds.length > 1 ? 's' : ''
                        } selecionado${detailsCheckedClientIds.length > 1 ? 's' : ''}`
                      : ''}
                  </Typography>
                  {detailsClientOptions.length > 0 &&
                    (() => {
                      const q = detailsModalSearch.toLowerCase();
                      const filtered = detailsClientOptions.filter(
                        c =>
                          c.cnpj.includes(q) ||
                          c.razao_social.toLowerCase().includes(q) ||
                          (c.nickname || '').toLowerCase().includes(q)
                      );
                      if (filtered.length === 0) return null;
                      const allSelected = filtered.every(c => detailsCheckedClientIds.includes(c.id));
                      return (
                        <Button
                          size='small'
                          disabled={isAddingClient}
                          onClick={() => {
                            if (allSelected) {
                              setDetailsCheckedClientIds(prev => prev.filter(id => !filtered.some(c => c.id === id)));
                            } else {
                              setDetailsCheckedClientIds(prev =>
                                Array.from(new Set([...prev, ...filtered.map(c => c.id)]))
                              );
                            }
                          }}
                        >
                          {allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                        </Button>
                      );
                    })()}
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '14px 20px' }}>
          <Button onClick={handleCloseDetails} disabled={isAddingClient}>
            Fechar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirmAddClients}
            disabled={isAddingClient || !hasDetailsChanges}
            disableElevation
            startIcon={isAddingClient ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap', py: 0.7, '&:hover': { backgroundColor: primary.hover } }}
          >
            {isAddingClient ? 'Salvando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={profileToDelete !== null} onClose={() => !isDeleting && setProfileToDelete(null)}>
        <DialogTitle>Remover perfil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover o perfil <strong>{profileToDelete?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ paddingTop: 5, marginBottom: 10, marginRight: 12 }}>
          <Button onClick={() => setProfileToDelete(null)} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={confirmDeleteProfile}
            disabled={isDeleting}
            disableElevation
            startIcon={isDeleting ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap' }}
          >
            {isDeleting ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProfilesTab;
